import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, MessageCircle } from "lucide-react";
import { CATEGORIES, PRODUCTS } from "@/data/siteData";
import { api, normalizeProduct, parsePrice, slugify } from "@/lib/api";
import { useSite, waLinkFor } from "@/lib/SiteContext";
import { ChapterHeading, Em, Reveal } from "./primitives";

const SORTS = [
  { id: "default", label: "Default" },
  { id: "latest", label: "Latest" },
  { id: "price-asc", label: "Price: Low to High" },
  { id: "price-desc", label: "Price: High to Low" },
];

export default function Products({ content, category, onCategoryChange }) {
  const { categories, contact } = useSite();
  const navigate = useNavigate();
  const openProduct = (p) => navigate(`/product/${p.slug || slugify(p.name)}`);
  const [remote, setRemote] = useState(null);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("default");
  const c = content || {};

  useEffect(() => {
    let alive = true;
    api
      .get("/products")
      .then((r) => {
        if (alive) setRemote(r.data.map(normalizeProduct));
      })
      .catch(() => {
        /* fall back to the built-in product list */
      });
    return () => {
      alive = false;
    };
  }, []);

  const filters = [
    { id: "all", name: "All Gifts" },
    ...categories.map((cat) => ({ id: cat.slug || cat.id, name: cat.name })),
  ];
  const catName = (slug) =>
    categories.find((cat) => (cat.slug || cat.id) === slug)?.name ||
    CATEGORIES.find((cat) => cat.id === slug)?.name ||
    slug;

  const source = remote || PRODUCTS;
  const visible = useMemo(() => {
    let list = category === "all" ? [...source] : source.filter((p) => p.category === category || p.categories?.includes(category));
    const min = parseInt(minPrice, 10);
    const max = parseInt(maxPrice, 10);
    if (!Number.isNaN(min) && minPrice !== "") list = list.filter((p) => parsePrice(p.price) >= min);
    if (!Number.isNaN(max) && maxPrice !== "") list = list.filter((p) => parsePrice(p.price) <= max);
    if (sort === "latest") list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "") || Number(b.id) - Number(a.id));
    if (sort === "price-asc") list.sort((a, b) => parsePrice(a.price) - parsePrice(b.price));
    if (sort === "price-desc") list.sort((a, b) => parsePrice(b.price) - parsePrice(a.price));
    return list;
  }, [source, category, minPrice, maxPrice, sort]);

  return (
    <section id="shop" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <ChapterHeading
          number={(c.kicker || "02 — The Collection").split("—")[0].trim()}
          kicker={(c.kicker || "02 — The Collection").split("—")[1]?.trim() || "The Collection"}
          title={<Em text={c.title || "Featured *Gifts*"} />}
          blurb={c.blurb || "A small, honest collection. Every piece can be personalized with names, photos and messages."}
        />

        <Reveal className="mb-6 flex flex-wrap gap-3">
          {filters.map((f) => (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => onCategoryChange(f.id)}
              className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                category === f.id
                  ? "border-ink bg-ink text-cream"
                  : "border-line bg-transparent text-[#5C5C5C] hover:border-rose hover:text-rose"
              }`}
            >
              {f.name}
            </button>
          ))}
        </Reveal>

        <Reveal className="mb-12 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-full border border-line bg-cream/60 px-4 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C5C5C]">Price ₹</span>
            <input
              data-testid="filter-price-min"
              type="number"
              min="0"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              placeholder="Min"
              className="w-20 bg-transparent text-sm outline-none"
              aria-label="Minimum price"
            />
            <span className="text-[#5C5C5C]">–</span>
            <input
              data-testid="filter-price-max"
              type="number"
              min="0"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="Max"
              className="w-20 bg-transparent text-sm outline-none"
              aria-label="Maximum price"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Sort products">
            {SORTS.map((s) => (
              <button
                key={s.id}
                data-testid={`sort-${s.id}`}
                onClick={() => setSort(s.id)}
                aria-pressed={sort === s.id}
                className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors duration-300 ${
                  sort === s.id
                    ? "border-rose bg-rose text-white"
                    : "border-line text-[#5C5C5C] hover:border-rose hover:text-rose"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div
          key={`${category}-${sort}-${minPrice}-${maxPrice}`}
          data-testid="product-grid"
          className="grid grid-cols-2 gap-5 md:gap-8 lg:grid-cols-4"
        >
          {visible.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.07}>
              <article
                data-testid={`product-card-${p.id}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-white transition-shadow duration-500 hover:shadow-xl hover:shadow-rose/10"
              >
                <button
                  data-testid={`product-view-${p.id}`}
                  onClick={() => openProduct(p)}
                  className="relative block overflow-hidden"
                  aria-label={`View details of ${p.name}`}
                >
                  <img
                    src={p.image}
                    alt={p.alt}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-ink/0 opacity-0 transition-all duration-500 group-hover:bg-ink/20 group-hover:opacity-100">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-ink">
                      <Eye size={18} />
                    </span>
                  </span>
                </button>
                <div className="flex flex-1 flex-col gap-2 p-4 md:p-5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-rose">
                    {catName(p.category)}
                  </span>
                  <h3 className="font-serif text-lg font-medium leading-snug text-ink md:text-xl">
                    {p.name}
                  </h3>
                  <p className="line-clamp-2 text-xs leading-relaxed text-[#5C5C5C] md:text-sm">
                    {p.description}
                  </p>
                  <p
                    data-testid={`product-price-${p.id}`}
                    className="mt-1 font-serif text-lg font-semibold text-ink"
                  >
                    {p.price}
                  </p>
                  <div className="mt-auto flex gap-2 pt-2">
                    <button
                      data-testid={`product-details-button-${p.id}`}
                      onClick={() => openProduct(p)}
                      className="flex-1 rounded-full border border-ink px-3 py-2.5 text-xs font-medium text-ink transition-colors duration-300 hover:bg-ink hover:text-cream"
                    >
                      View Details
                    </button>
                    <a
                      data-testid={`product-enquire-button-${p.id}`}
                      href={waLinkFor(contact, `Hello! I'd like to enquire about the ${p.name} (${p.price}).`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Enquire about ${p.name} on WhatsApp`}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose text-white transition-colors duration-300 hover:bg-rose-dark"
                    >
                      <MessageCircle size={16} />
                    </a>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
          {visible.length === 0 && (
            <p data-testid="products-empty" className="col-span-full py-16 text-center text-sm text-[#5C5C5C]">
              No products match these filters.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
